import { api } from '@/lib/api'
import { setAuthToken, clearAuthToken } from '@/lib/tokenRefresh'
import { User, ApiResponse } from '@/types'

export interface RegisterData {
  // Credenciais e identidade básica
  name: string
  email: string
  password: string

  // Identificação extra (opcionais para retrocompatibilidade)
  username?: string
  cpf?: string
  phone?: string

  // Localização e perfil declarado
  city?: string
  state?: string
  profession?: string
  profileType?: string
  howKnew?: string
  aiFamiliarity?: string

  // Aceite legal LGPD
  termsAccepted?: boolean
  termsAcceptedAt?: string
  termsVersion?: string
  termsAcceptedUserAgent?: string
  privacyAcceptedAt?: string
  privacyVersion?: string
}

export interface ValidateRegistrationData {
  email: string
  cpf?: string
  username?: string
}

export interface LoginData {
  email: string
  password: string
}

export interface AuthResponse {
  user: {
    id: string
    email: string
    name: string
    role: string
    permissions: string[]
  }
  tokens?: {
    accessToken: string
    refreshToken: string
  }
  accessToken?: string
  refreshToken?: string
}

export const authService = {
  // Validar dados antes do registro
  async validateRegistration(data: ValidateRegistrationData): Promise<{ success: boolean; details?: Record<string, string> }> {
    try {
      const response = await api.post('/auth/validate', data)
      return response.data
    } catch (error: any) {
      // 409 = duplicidade (email/cpf/username já em uso)
      if (error.response?.status === 409) {
        return {
          success: false,
          details: error.response.data.details
        }
      }

      // 400 = formato inválido — tratar como falha de validação por campo
      // para não cair em "Dados inválidos" genérico.
      if (error.response?.status === 400 && Array.isArray(error.response.data?.details)) {
        const details: Record<string, string> = {}
        for (const item of error.response.data.details) {
          if (item?.field && item?.message) {
            details[item.field] = item.message
          }
        }
        return { success: false, details }
      }

      throw error
    }
  },

  async login(data: LoginData): Promise<AuthResponse> {
    const response = await api.post<ApiResponse<AuthResponse>>('/auth/login', data)
    
    // Verificar se a resposta tem erro
    if (response.data.error) {
      throw new Error(response.data.error)
    }
    
    // O backend retorna direto user e tokens (sem wrapper data)
    if ((response.data as any).user && (response.data as any).tokens) {
      const authData = response.data as any
      // Salvar tokens
      if (authData.tokens?.accessToken) {
        setAuthToken(authData.tokens.accessToken)
      }
      return authData
    }

    // Verificar se tem data (formato antigo)
    if (!response.data.data) {
      throw new Error('Resposta inválida do servidor')
    }

    const authData = response.data.data

    // Salvar tokens
    if (authData.tokens?.accessToken) {
      setAuthToken(authData.tokens.accessToken)
    }

    return authData
  },

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await api.post<ApiResponse<AuthResponse>>('/auth/register', data)

    // Verificar se a resposta tem erro
    if (response.data.error) {
      throw new Error(response.data.error)
    }

    // O backend retorna direto user e tokens (sem wrapper data)
    if ((response.data as any).user && (response.data as any).tokens) {
      const authData = response.data as any
      // Salvar tokens
      if (authData.tokens?.accessToken) {
        setAuthToken(authData.tokens.accessToken)
      }
      return authData
    }

    // Verificar se tem data (formato antigo)
    if (!response.data.data) {
      throw new Error('Resposta inválida do servidor')
    }

    const authData = response.data.data

    // Salvar tokens
    if (authData.tokens?.accessToken) {
      setAuthToken(authData.tokens.accessToken)
    }

    return authData
  },

  async logout(): Promise<void> {
    clearAuthToken()
    try {
      await api.post('/auth/logout', {})
    } catch {
      // Sessão já limpa localmente — ignorar erros do servidor
    }
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get<ApiResponse<User>>('/auth/me')
    
    // O backend retorna {user: {...}, permissions: [...]}
    if ((response.data as any).user) {
      return (response.data as any).user
    }
    
    // O backend retorna direto o usuário (sem wrapper data)
    if ((response.data as any).id || (response.data as any).email) {
      return response.data as User
    }
    
    // Verificar se tem data (formato antigo)
    if (!response.data.data) {
      throw new Error('Usuário não encontrado')
    }
    
    return response.data.data
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await api.post('/auth/forgot-password', { email })
    return response.data
  },

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    const response = await api.post('/auth/reset-password', { token, password })
    return response.data
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response = await api.post('/auth/change-password', {
      currentPassword,
      newPassword
    })
    return response.data
  },

  async deleteAccount(password: string): Promise<void> {
    await api.delete('/auth/profile/delete-account', { data: { password } })
    clearAuthToken()
  },

  getToken(): string | null {
    return localStorage.getItem('auth_token')
  },

  isAuthenticated(): boolean {
    return !!this.getToken()
  }
}